import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";

import TuneIcon from "@mui/icons-material/Tune";
import { Button, ClickAwayListener, IconButton, Paper, Popper, Stack } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { calcFieldsToDisplay } from "constants/reports";
import { union } from "lodash";

import { ReportsQueries } from "../../../store/types";

type GroupByMenuProps = React.PropsWithChildren<{
  children: (React.ReactElement | null) | (React.ReactElement | null)[];
  // setOptions: (value: Partial<ReportsQueries>) => void;
  setOptions: Dispatch<SetStateAction<Partial<ReportsQueries>>>;
  disabled?: boolean;
  icon?: React.ReactElement;
  tooltip?: string;
  renderTrigger?: (params: { onClick: React.MouseEventHandler }) => React.ReactElement;
}>;

const PADDING = 16;
const MIN_HEIGHT = 250;

export const ReportGroupByMenu: React.FC<GroupByMenuProps> = ({
  children,
  setOptions,
  disabled,
  icon = <TuneIcon />,
  tooltip = "Table Configuration",
  renderTrigger
}) => {
  const [values, setValues] = useState<Partial<ReportsQueries>>({});
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [placement, setPlacement] = useState<"bottom-start" | "top-start">("bottom-start");

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - PADDING;
    const spaceAbove = rect.top - PADDING;

    if (spaceBelow >= MIN_HEIGHT) {
      setPlacement("bottom-start");
    } else if (spaceAbove > spaceBelow) {
      setPlacement("top-start");
    } else {
      setPlacement("bottom-start");
    }

    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "group-by-popover" : undefined;

  // Lock page scroll when popper is open, but allow scrolling inside popper/dropdowns
  useEffect(() => {
    if (!open) return;
    const prevent = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.closest(".MuiAutocomplete-listbox, .MuiAutocomplete-popper")) return;
      e.preventDefault();
    };
    document.addEventListener("wheel", prevent, { passive: false });
    document.addEventListener("touchmove", prevent, { passive: false });
    return () => {
      document.removeEventListener("wheel", prevent);
      document.removeEventListener("touchmove", prevent);
    };
  }, [open]);

  const submitValues = useCallback(() => {
    setOptions((prev) => {
      //if calcFieldsToDisplay was active from toolbar we need to keep it into main state, in case the measure autocomplete keep the rest of parameters (exclude calcFieldsToDisplay)
      const preselectedCalcFields = calcFieldsToDisplay.filter((item) => prev.calculatedFields?.includes(item));
      const calcFields = union(values.calculatedFields, preselectedCalcFields);

      return { ...prev, ...values, calculatedFields: calcFields };
    });
    handleClose();
  }, [values]);

  return (
    <>
      {renderTrigger ? (
        React.createElement(renderTrigger, {
          onClick: handleClick
        })
      ) : (
        <MaterialTooltip isShow title={tooltip} placement="top">
          <IconButton
            // sx={{ opacity: "0" }}
            disabled={disabled}
            className="group-by__tune"
            id="group-by-btn"
            aria-describedby={id}
            onClick={handleClick}
          >
            {icon}
          </IconButton>
        </MaterialTooltip>
      )}
      <Popper id={id} open={open} anchorEl={anchorEl} placement={placement} sx={{ zIndex: 1300 }}>
        <Paper elevation={8}>
          <ClickAwayListener onClickAway={handleClose}>
            <div className="group-by-menu">
              {/* <div className="head indent">Deal Measures Configuration</div> */}
              <div className="body indent">
                {Array.isArray(children)
                  ? children.map((child, index) => {
                      return (
                        child &&
                        React.cloneElement(child, {
                          key: index,
                          onChangeDataFormation: (df: Partial<ReportsQueries>) => setValues({ ...values, ...df })
                        })
                      );
                    })
                  : children &&
                    React.cloneElement(children, {
                      onChangeDataFormation: (df: Partial<ReportsQueries>) => setValues({ ...values, ...df })
                    })}
              </div>
              <div className="footer indent">
                <Stack flexDirection="row" justifyContent="flex-end" alignItems="center" gap="10px">
                  <Button size="sm" variant="outlined" sx={{ minWidth: "53px" }} onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    onClick={submitValues}
                    size="sm"
                    variant="contained"
                    sx={{ backgroundColor: "#071F35", minWidth: "46px" }}
                  >
                    Okay
                  </Button>
                </Stack>
              </div>
            </div>
          </ClickAwayListener>
        </Paper>
      </Popper>
    </>
  );
};
